import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { Container, Header, Segment, Divider, Icon, List, Grid, Card, Message, Table } from 'semantic-ui-react';
import './Playbook.css';

class Closing extends Component {
    render() {
        return (
            <Container className="playbook-section-container">
                <h1 className="ui icon center aligned header playbook-branded-header" style={{fontFamily: 'Montserrat', textTransform: 'uppercase', color: 'rgb(35, 37, 58)', fontWeight: 600, fontSize: '38px', marginBottom: '-9px'}}>
                    <i aria-hidden="true" className="icon">
                        <img src="/static/media/logo-icon.7bb8994723bbdcd674f1.png" alt="logo-icon" style={{height: '68px', marginBottom: '-15px'}} />
                    </i>
                    Closing
                </h1>
                <div className="ui divider"></div>

                <Segment className="playbook-content-segment">
                    <Header as="h2" color="teal">The Proposal – Explained</Header>
                    
                    <Header as="h3">Closing</Header>
                    <p>
                        A well-written proposal is among the most effective sales tools one can deploy. It is how you close. The reason why—buying 
                        decisions are most often made when the seller is NOT in the room. Therefore, to give the seller the best chance to succeed, 
                        he/she must equip his/her key stakeholder with enough information to "carry the message properly." That's where the proposal comes in.
                    </p>

                    <Message info>
                        <Message.Header>Critical Insight</Message.Header>
                        <p>
                            When you think of a proposal and its value, envision the point in a sales cycle where the seller has done his/her job. 
                            The sales meetings are complete, the product or service has been pitched, pricing and timelines have been discussed, and 
                            now the prospect needs to make a decision.
                        </p>
                    </Message>

                    <p>
                        The prospect will likely hold a meeting with influencers and decision makers, some of which have not been in any of the sales 
                        meetings. This is where the decision to buy will occur. Therefore, the seller has two choices:
                    </p>

                    <Grid stackable columns={2}>
                        <Grid.Column>
                            <Card color="red">
                                <Card.Content>
                                    <Card.Header>Bad Choice</Card.Header>
                                    <Card.Description>
                                        Outsource the seller's message and hope the key stakeholder represents the seller the right way internally. 
                                        This method creates confusion.
                                    </Card.Description>
                                </Card.Content>
                            </Card>
                        </Grid.Column>

                        <Grid.Column>
                            <Card color="green">
                                <Card.Content>
                                    <Card.Header>Good Choice</Card.Header>
                                    <Card.Description>
                                        Equip the key stakeholder to represent the company, the solution, and pricing with the seller's input. 
                                        This method creates success.
                                    </Card.Description>
                                </Card.Content>
                            </Card>
                        </Grid.Column>
                    </Grid>

                    <Segment color="teal">
                        <Header as="h4">Universal Application</Header>
                        <p>
                            And just in case you are thinking "our sales process is too complex for a simple proposal," this process and these templates 
                            have been successfully used to sell multi-million-dollar complex IT solutions to enterprise corporations, SaaS solutions to 
                            startups, services to companies big and small, and everything in between.
                        </p>
                        <p>
                            <strong>Once you get to the point of buying, you must be able to create a document that simplifies the complex.</strong>
                        </p>
                    </Segment>

                    <Divider />

                    <Header as="h3" color="teal">What the proposal is NOT</Header>
                    <p>Let's first start with what the proposal is NOT. This may make things a little simpler as you think through it:</p>

                    <List bulleted>
                        <List.Item><strong>The proposal is not your sales material.</strong> You have already provided that. The proposal is the executive summary of the sales material combined with how the product/service fits into the buyer's organization.</List.Item>
                        <List.Item><strong>The proposal is not a marketing slick.</strong> You will see in our examples that we have a couple images representing the seller and the prospect, but this isn't a glossy document.</List.Item>
                        <List.Item><strong>The proposal is not a detailed project plan</strong> that gets into the weeds of a project. It describes the activities and/or functions of a product, service, or solution.</List.Item>
                        <List.Item><strong>It is not a long document filled with boilerplate information</strong> built by the marketing team. It's a client- and opportunity-specific document built by the seller.</List.Item>
                        <List.Item><strong>It is not a legal document.</strong> It's not binding, there is no signature blank, and it doesn't contain a bunch of legal language. That's the contract.</List.Item>
                    </List>

                    <Message warning>
                        <Message.Header>Important Note</Message.Header>
                        <p>
                            When done right, the proposal becomes the basis of the contract. (Though it should be noted that we have had cases where 
                            we submitted a proposal and had a multibillion-dollar company just sign the bottom and send it back.)
                        </p>
                    </Message>

                    <p>The proposal is not a document that assumes the audience knows the seller or the problem being solved. It is actually designed as an "executive summary" for an audience that does NOT know the seller or problem being solved.</p>

                    <Divider />

                    <Header as="h3" color="teal">What is the proposal</Header>
                    <p>So now let's look at what a proposal is. Don't overthink this.</p>

                    <p>The proposal consists of three primary sections:</p>

                    <Card.Group itemsPerRow={4}>
                        <Card color="blue">
                            <Card.Content textAlign="center">
                                <Icon name="info circle" size="large" />
                                <Card.Header>Introduction</Card.Header>
                                <Card.Description>Orient the reader into the sales cycle</Card.Description>
                            </Card.Content>
                        </Card>

                        <Card color="green">
                            <Card.Content textAlign="center">
                                <Icon name="cogs" size="large" />
                                <Card.Header>Body</Card.Header>
                                <Card.Description>Description of solution</Card.Description>
                            </Card.Content>
                        </Card>

                        <Card color="orange">
                            <Card.Content textAlign="center">
                                <Icon name="dollar sign" size="large" />
                                <Card.Header>Pricing & Schedule</Card.Header>
                                <Card.Description>Costs and timeline</Card.Description>
                            </Card.Content>
                        </Card>

                        <Card color="purple">
                            <Card.Content textAlign="center">
                                <Icon name="list" size="large" />
                                <Card.Header>Assumptions</Card.Header>
                                <Card.Description>Dependencies and conditions</Card.Description>
                            </Card.Content>
                        </Card>
                    </Card.Group>

                    <Divider />

                    <Header as="h4" color="blue">Introduction</Header>
                    <p>The primary purpose of the introduction section is to orient the reader into the sales cycle. The introduction should include:</p>
                    <List bulleted>
                        <List.Item>A brief description of the buying company and the problem being solved.</List.Item>
                        <List.Item>A brief description of the seller and the product/services it provides.</List.Item>
                        <List.Item>A brief sentence or two describing the purpose of the proposal.</List.Item>
                    </List>

                    <p>
                        The purpose of the introduction section is such that as the new decision maker(s) or influencer(s) enters the sales cycle, 
                        they can quickly come up to speed on the seller and the problem being solved.
                    </p>

                    <Segment>
                        <p>
                            If you are a close participant in the sales cycle, you will see in our examples that we are stating some pretty obvious 
                            things. But the proposal is aimed at the meeting where we (as sellers) are not present. Our hope is that the main prospect 
                            shares our proposal with decision makers or influencers, and when that happens, the new audience is very quickly oriented 
                            into the sales cycle.
                        </p>
                    </Segment>

                    <Header as="h4" color="green">Body</Header>
                    <p>
                        This is the primary content of the proposal. It is not more or less important than other aspects of the document, but this 
                        is where the seller must describe an often complex solution in simple terms. This is not supposed to be a detailed dissertation, 
                        a project plan, or other detailed document. This is a few paragraphs that describe the solution being sold.
                    </p>

                    <p>
                        When building this section, the seller should think through high-level the activities that must occur to implement a product 
                        or execute a service. This type of information can describe a methodology used by the seller or custom activities that the 
                        seller will undertake to complete the project.
                    </p>

                    <Message info>
                        <Message.Header>Dependencies</Message.Header>
                        <p>
                            Most every project has dependencies on the buyer. This is where you want to state the critical dependencies. When writing, 
                            the seller does not want to call them out robotically but instead should do so as a part of the description of the solution.
                        </p>
                    </Message>

                    <Header as="h4" color="orange">Pricing and Schedule</Header>
                    <p>
                        This is perhaps the easiest portion of the proposal. When a prospect reads this section, they should be able to see exactly 
                        how they will be charged, the schedule they will be charged on, and the duration of the engagement.
                    </p>

                    <p>It is also important to tell the customer what they will be getting with the engagement. A quick bulleted list of engagements includes things like:</p>
                    <List bulleted>
                        <List.Item>Status reports (or modes of formal communication)</List.Item>
                        <List.Item>The product you are selling</List.Item>
                        <List.Item>Anything tangible that will be delivered to the buyer</List.Item>
                    </List>

                    <Header as="h4" color="purple">Assumptions</Header>
                    <p>
                        This section is for smaller level things that may affect the project and as such, can be optional. If you, as the seller, 
                        are making large assumptions that affect the success of the solution, then those should be mentioned in the body. However, 
                        there could be smaller assumptions that you are making. Those would go as bulleted items in this section.
                    </p>

                    <Divider />

                    <Header as="h3" color="teal">Why a proposal</Header>
                    <p>
                        Quite often, this is the first time you as a seller get to demonstrate your professionalism and capabilities. When a prospect 
                        sees a professional proposal built to the opportunity, it increases the chances for success.
                    </p>

                    <List bulleted>
                        <List.Item>Demonstrates professionalism and capabilities</List.Item>
                        <List.Item>Sets expectations for delivery</List.Item>
                        <List.Item>Becomes the basis of the eventual contract</List.Item>
                        <List.Item>Serves as a valuable tool to qualify prospects externally</List.Item>
                        <List.Item>Helps qualify opportunities in the pipeline internally</List.Item>
                    </List>

                    <Header as="h4">Internal Qualification Tool</Header>
                    <p>
                        If you as a company leader or sales manager are reviewing your pipeline in CRM and/or speaking with your sales team about 
                        their activity, it is very easy to use subjective information to determine the phase of the sales cycle. It's not the case 
                        with every company we've worked with, but usually we help companies see the value in fleshing out sales data with the addition 
                        of the simple question:
                    </p>

                    <Message success>
                        <Message.Header>Key Question</Message.Header>
                        <p><strong>Does the prospect have a proposal?</strong></p>
                    </Message>

                    <Table celled>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>Answer</Table.HeaderCell>
                                <Table.HeaderCell>Verification Ability</Table.HeaderCell>
                                <Table.HeaderCell>Information Quality</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            <Table.Row positive>
                                <Table.Cell><strong>Yes</strong></Table.Cell>
                                <Table.Cell>Can review the proposal and VERIFY that the salesperson has described the problem, solution, and provided pricing</Table.Cell>
                                <Table.Cell>Objective, verifiable information</Table.Cell>
                            </Table.Row>
                            <Table.Row negative>
                                <Table.Cell><strong>No</strong></Table.Cell>
                                <Table.Cell>Salesperson says "we have discussed all these things and they are interested" but it's not verifiable</Table.Cell>
                                <Table.Cell>Subjective, unverifiable information</Table.Cell>
                            </Table.Row>
                        </Table.Body>
                    </Table>

                    <Header as="h4">External Qualification Tool</Header>
                    <p>
                        Adding the requirement that the sales team produce proposals can turn subjective information into objective information, 
                        which enhances the management capabilities and predictability of the sales information.
                    </p>

                    <p>Not only does it assist with creating better information internally, it also assists with qualifying out a potential prospect. If we are selling and we have a prospect that demonstrates interest and we have discussed solution-based details, then we can ask:</p>

                    <Segment color="green">
                        <Header as="h4">The Qualifying Question</Header>
                        <p><strong>"Would you like for me to put a proposal together of what a project would look like?"</strong></p>
                    </Segment>

                    <p>This demonstrates our willingness to do work on behalf of the prospect. If the prospect is interested but not a buyer, then the prospect will likely say "no" to the offer. There are a couple of reasons for this:</p>
                    <List numbered>
                        <List.Item>They don't want the seller to waste time.</List.Item>
                        <List.Item>Having a proposal in hand can put pressure on the prospect. They can potentially feel some obligation to circulate and/or feel an obligation to give you a "yes" or "no."</List.Item>
                    </List>

                    <p>
                        If a prospect does want a proposal, then it gives the seller all the "rights" to follow up until the seller gets the 
                        definitive answer we all are looking for.
                    </p>

                    <Divider />

                    <Header as="h3" color="teal">Sending the Proposal</Header>
                    <p>
                        The thing you need to realize is that when you create a proposal, knowing when to follow up can be difficult. We don't like 
                        to feel like we are chasing, but we want to know the prospect got it. So how do we do that? We tell them when we will follow 
                        up when we send the proposal.
                    </p>

                    <Segment>
                        <Header as="h5">Example Proposal Email</Header>
                        <div style={{ fontFamily: 'monospace', background: '#f8f8f8', padding: '15px', borderRadius: '5px' }}>
                            Tom / Sarah – Great meeting you guys the other day. I love what you all are doing and am glad you guys found the ABC Corp crowd. 
                            Per our conversation, I have attached the proposal for a message-creation and training engagement. Here are the high points:<br/><br/>
                            
                            • 3-month engagement – 1st 4-6 weeks building out the messaging / 2nd 6-8 weeks training and applying concepts in customer settings<br/>
                            • Train a team of 4-6<br/>
                            • Meet formally once a week with impromptu meetings throughout the week<br/>
                            • You all get all of our processes to continue post Multiply if you choose<br/>
                            • Price of $XX,XXX<br/>
                            • Start date some time after June 15 pending schedules.<br/><br/>
                            
                            I definitely see how this process can help as you merge companies and differentiate the staffing and solutioning capabilities. 
                            Feel free to reach out with questions, or I'll follow up in the next couple days if I don't hear back from you.<br/><br/>
                            
                            Thanks and I look forward to our next discussion.<br/><br/>
                            
                            Ryan
                        </div>
                    </Segment>

                    <p>When we send a proposal we want to:</p>
                    <List bulleted>
                        <List.Item>Be clear and concise</List.Item>
                        <List.Item>Boil the contents of the proposal down into a few bullet points.</List.Item>
                        <List.Item>Include a start date</List.Item>
                        <List.Item>Set expectations on when YOU will follow up.</List.Item>
                    </List>

                    <Message info>
                        <Message.Header>Mobile-First Approach</Message.Header>
                        <p>
                            Expect the prospect to get the proposal email on his/her phone. The bullets allow them to take a quick look and get the 
                            gist; then they can then go back and review later.
                        </p>
                    </Message>

                    <p>
                        When you send your proposal, give the prospect time to read it, process it, and react. Tell the prospect to contact you if 
                        he or she has any questions, comments, etc. Otherwise you will check back in a week. If I send a proposal on a Wednesday, 
                        then I typically say: "Take a look and text, call, or email if you have any questions or want to discuss further. Otherwise, 
                        I will follow up with you next Wednesday."
                    </p>

                    <Divider />

                    <Header as="h3" color="teal">Conclusion</Header>
                    <p>
                        The proposal process is powerful. There is no downside. The concepts are simple, but they are not natural. The initial 
                        proposals can take time, but it will quickly become routine. The content begins to repeat itself, the thought process becomes 
                        natural, and the document template is built. Sales absolutely follow.
                    </p>

                    <Segment color="teal">
                        <Header as="h4">Final Thought</Header>
                        <p>
                            As salespeople, we never want to "outsource our message" to someone else. And if decisions are made when we are not in 
                            the room, then we have to "speak" when we are not there.
                        </p>
                    </Segment>
                </Segment>
            </Container>
        );
    }
}

export default observer(Closing);